import React from "react";

import { useState, useMemo, useEffect } from "react";

import "./Grids.css";
import {
  FaSortAmountUpAlt,
  FaSortAmountDown,
  FaSort,
  FaPlusCircle,
  FaFileExcel,
  FaTimesCircle,
} from "react-icons/fa";

import {
  Column,
  Table,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  FilterFn,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Switch } from "@progress/kendo-react-inputs";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";

import { clientsGrid } from "../../data/interfaces";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    numberRange: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

// Simple numberRange filter - not used in this grid but required by the interface
const numberRangeFilter: FilterFn<any> = (row, columnId, value) => {
  return true; // Always return true since we don't use number range filtering in this grid
};

function ClientsGrid(props: any) {
  const [showDeleted, setShowDeleted] = useState(false);
  const [data, setData] = useState<clientsGrid[]>([]);

  console.log("ClientsGrid", data);

  const handleShowDeleted = (event: any) => {
    setShowDeleted(event.target.value);
    props.onShowDeleted(event.target.value);
  };

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const clientColumns = useMemo<ColumnDef<clientsGrid>[]>(
    () => [
      {
        accessorKey: "clientName",
        header: "Client Name",
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "accountRef",
        header: "Account Ref",
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "clientAddr",
        header: "Client address",
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "what3Words",
        header: "what3words",
        footer: (props) => props.column.id,
      },      
      {
        accessorKey: "phone",
        header: "Phone",
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "email",
        header: "Email",
        footer: (props) => props.column.id,
      },
    ],
    []
  );

  useEffect(() => {
    setData(props.onData);
  }, [props.onData]);

  const table = useReactTable({
    data,
    columns: clientColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
      numberRange: numberRangeFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  return (
    <div className="p-2">
      {/* This is the global column search box */}
      <div className="grid__title__container">
        <div className="grid__header__background">
          <div className="grid__title">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div>Clients</div>
              <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowDeleted}
                defaultChecked={false}
              />
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaTimesCircle
                  style={{
                    fontSize: "14pt",
                    marginLeft: "0.7rem",
                    color: showDeleted ? "rgb(28 22 7)" : "rgb(179 166 131)",
                  }}
                  title="Show Deleted Clients?"
                />
              </Tooltip>
            </div>
            <div style={{ display: "flex", flexDirection: "row" }}>
              <div
                onClick={() => props.onAddToggle()}
                className="grid__addrecord__button"
              >
                <FaPlusCircle
                  style={{ fontSize: "14pt", marginRight: "1rem" }}
                />{" "}
                <p style={{ margin: 0 }}>Add new client</p>
              </div>
              <div
                onClick={() => props.onExport()}
                className="grid__export__button"
              >
                <FaFileExcel
                  style={{ fontSize: "14pt", marginRight: "1rem" }}
                />{" "}
                <p style={{ margin: 0 }}>Export clients</p>
              </div>
            </div>
          </div>
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => setGlobalFilter(String(value))}
            placeholder="Search all client columns..."
            className="filter__input__global"
          />
        </div>
      </div>

      <div className="h-2" />
      <table className="grid__table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <>
                        {/* This DIV is where you hover/click on the column header to sort */}
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "header__sortable"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <FaSortAmountUpAlt style={{ fontSize: "14pt" }} />
                            ),
                            desc: (
                              <FaSortAmountDown style={{ fontSize: "14pt" }} />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <FaSort style={{ fontSize: "14pt" }} />
                          )}
                        </div>

                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const bgColor = () => {
              if (row.original.deletedDate) {
                return "rgba(235, 199, 199, 0.5)";
              } else {
                return "rgb(245 249 251)";
              }
            };

            return (
              <tr
                onDoubleClick={() => {
                  props.onEditToggle(row.original.clientId);
                }}
                key={row.id}
                style={{
                  backgroundColor: bgColor(),
                  border: "1px solid rgb(216 229 237)",
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const isDeleted = cell.row.original.deletedDate;

                  const textColor = isDeleted ? "rgb(137 5 5)" : "#1e2a4e";
                  return (
                    <td style={{ color: textColor }} key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* This is the pagination controls */}
      <div className="page__container">
        <div className="page__button__container">
          <button
            className="page__button"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="page__button"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>
        </div>

        <div className="page__controls">
          <div className="page__numbers">
            <div>Page&nbsp;</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </div>
          <div className="page__numbers__goto">
            <div className="page__numbers__gototext">| Go to page:</div>
            <input
              type="number"
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="filter__input"
            />
          </div>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="page__select"
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column.getFacetedUniqueValues()]
  );

  return typeof firstValue === "number" ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.slice(0, 5000).map((value: any) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="filter__input"
        list={column.id + "list"}
      />
    </>
  );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

export default ClientsGrid;
